import { Box, Container, Grid, IconButton, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import OpportunityAccordion from 'components/opportunities/OpportunityAccordion';
import { ApplyInstructionsType, IScrapedDetails, OpportunityType } from 'global/interfaces/opportunity';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getScrapeDetails } from 'services/opportunityService';
import { showError } from 'utils/errorHandler';
import OppNotFound from './OppNotFound';
import OpenButton from 'components/common/openButton';
import { RoundButton } from 'components/common/Button/RoundButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';

const JustifiedBox = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export default function ViewScrapedOpportunity() {
  const [scraped, setScraped] = useState<IScrapedDetails>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleDownload = () => {
    const json = JSON.stringify(scraped?.liveGig, null, 2); // Pretty print with 2 spaces
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = scraped?.liveGig.id + '.json';
    link.click();

    URL.revokeObjectURL(url); // Clean up the object URL
  };

  useEffect(() => {
    if (!id) return;

    getScrapeDetails(id)
      .then(res => setScraped(res))
      .catch(e => {
        if (e.status === 404) {
          setNotFound(true);
          return;
        }
        showError(e);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (notFound) {
    return <OppNotFound />;
  }

  return (
    <>
      {scraped && !loading && (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid container item xs={12} pb={2}>
              <Grid item xs={12}>
                <JustifiedBox>
                  <Typography variant="h6">Matches</Typography>
                  <IconButton title="Download a json file" onClick={handleDownload}>
                    <CloudDownloadOutlinedIcon sx={{ height: '32px', width: '32px' }} />
                  </IconButton>
                </JustifiedBox>
              </Grid>
              <Grid item xs={2}>
                <Stack direction={'column'} pt={2} spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Apply Info
                  </Typography>
                  <MatchIcon match={scraped.liveGig.applyInfoMatch} />
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Stack direction={'column'} pt={2} spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Location
                  </Typography>
                  <MatchIcon match={scraped.liveGig.locationMatch} />
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Stack direction={'column'} pt={2} spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Budget
                  </Typography>
                  <MatchIcon match={scraped.liveGig.budgetMatch} />
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Stack direction={'column'} pt={2} spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Categories
                  </Typography>
                  <MatchIcon match={scraped.liveGig.categoriesMatch} />
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Stack direction={'column'} pt={2} spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Title
                  </Typography>
                  <MatchIcon match={scraped.liveGig.titleMatch} />
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Stack direction={'column'} pt={2} spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Description
                  </Typography>
                  <MatchIcon match={scraped.liveGig.descriptionMatch} />
                </Stack>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Box mb={3}>
                <Typography variant="h6">Scraped Gig</Typography>
              </Box>
              <Box pb={1}>
                <OpportunityAccordion
                  opportunity={scraped.scrapedGig}
                  loggedIn={true}
                  expanded={true}
                  forceMobile={true}
                  allowCopy={true}
                >
                  <Stack direction="column" spacing={1.5} width="100%">
                    {scraped.scrapedGig.type !== OpportunityType.Shoutt && scraped.scrapedGig.source && (
                      <Stack direction="row" alignItems={'center'}>
                        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600}>
                          Source Url
                        </Typography>
                        <Stack ml={0.5}>
                          <OpenButton url={scraped.scrapedGig.source} />
                        </Stack>
                      </Stack>
                    )}
                    {(scraped.scrapedGig.type == OpportunityType.Shoutt ||
                      (scraped.scrapedGig.type == OpportunityType.External && scraped.scrapedGig.isAssisted)) && (
                      <Box>
                        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600} component={'span'}>
                          {scraped.scrapedGig.type == OpportunityType.External && scraped.scrapedGig.isAssisted
                            ? 'Applications Email:'
                            : 'Logged By Email: '}{' '}
                        </Typography>
                        <Typography variant={isMobile ? 'body2' : 'body1'} component={'span'} ml={1}>
                          {scraped.scrapedGig.type == OpportunityType.External && scraped.scrapedGig.isAssisted
                            ? scraped.scrapedGig.assistedEmail
                            : scraped.scrapedGig.email}
                        </Typography>
                      </Box>
                    )}
                    {scraped.scrapedGig.type == OpportunityType.External &&
                      !(scraped.scrapedGig.isAssisted ?? false) && (
                        <Box>
                          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600}>
                            Apply Instructions:{' '}
                          </Typography>
                          {scraped.scrapedGig.applyInstructionsType == ApplyInstructionsType.Url ? (
                            <Link to={scraped.scrapedGig.applyInstructions ?? ''} target="_blank">
                              <Typography
                                variant={isMobile ? 'body2' : 'body1'}
                                mt={0.5}
                                color={theme.palette.primary.dark}
                                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                              >
                                {scraped.scrapedGig.applyInstructions}
                              </Typography>
                            </Link>
                          ) : (
                            <Typography variant={isMobile ? 'body2' : 'body1'} mt={0.5}>
                              {scraped.scrapedGig.applyInstructions}
                            </Typography>
                          )}
                        </Box>
                      )}
                    {scraped.scrapedGig.internalNotes && (
                      <Box>
                        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600}>
                          Internal Notes:{' '}
                        </Typography>
                        <Typography variant={isMobile ? 'body2' : 'body1'} mt={0.5}>
                          {scraped.scrapedGig.internalNotes}
                        </Typography>
                      </Box>
                    )}

                    <Box pt={1}>
                      <RoundButton
                        variant="outlined"
                        onClick={() => navigate(`/admin/internal-jobs/${scraped.liveGig.id}`)}
                      >
                        Edit
                      </RoundButton>
                    </Box>
                  </Stack>
                </OpportunityAccordion>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box mb={3}>
                <Typography variant="h6">Live Gig</Typography>
              </Box>
              <Box pb={1}>
                <OpportunityAccordion
                  opportunity={scraped.liveGig}
                  loggedIn={true}
                  expanded={true}
                  forceMobile={true}
                  allowCopy={true}
                >
                  <Stack direction="column" spacing={1.5} width="100%">
                    {scraped.liveGig.type !== OpportunityType.Shoutt && scraped.liveGig.source && (
                      <Stack direction="row" alignItems={'center'}>
                        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600}>
                          Source Url
                        </Typography>
                        <Stack ml={0.5}>
                          <OpenButton url={scraped.liveGig.source} />
                        </Stack>
                      </Stack>
                    )}
                    {(scraped.liveGig.type == OpportunityType.Shoutt ||
                      (scraped.liveGig.type == OpportunityType.External && scraped.liveGig.isAssisted)) && (
                      <Box>
                        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600} component={'span'}>
                          {scraped.liveGig.type == OpportunityType.External && scraped.liveGig.isAssisted
                            ? 'Applications Email:'
                            : 'Logged By Email: '}{' '}
                        </Typography>
                        <Typography variant={isMobile ? 'body2' : 'body1'} component={'span'} ml={1}>
                          {scraped.liveGig.type == OpportunityType.External && scraped.liveGig.isAssisted
                            ? scraped.liveGig.assistedEmail
                            : scraped.liveGig.email}
                        </Typography>
                      </Box>
                    )}
                    {scraped.liveGig.type == OpportunityType.External && !(scraped.liveGig.isAssisted ?? false) && (
                      <Box>
                        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600}>
                          Apply Instructions:{' '}
                        </Typography>
                        {scraped.liveGig.applyInstructionsType == ApplyInstructionsType.Url ? (
                          <Link to={scraped.liveGig.applyInstructions ?? ''} target="_blank">
                            <Typography
                              variant={isMobile ? 'body2' : 'body1'}
                              mt={0.5}
                              color={theme.palette.primary.dark}
                              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              {scraped.liveGig.applyInstructions}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography variant={isMobile ? 'body2' : 'body1'} mt={0.5}>
                            {scraped.liveGig.applyInstructions}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {scraped.liveGig.internalNotes && (
                      <Box>
                        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600}>
                          Internal Notes:{' '}
                        </Typography>
                        <Typography variant={isMobile ? 'body2' : 'body1'} mt={0.5}>
                          {scraped.liveGig.internalNotes}
                        </Typography>
                      </Box>
                    )}

                    <Box pt={1}>
                      <RoundButton
                        variant="outlined"
                        onClick={() => navigate(`/admin/internal-jobs/${scraped.liveGig.id}`)}
                      >
                        Edit
                      </RoundButton>
                    </Box>
                  </Stack>
                </OpportunityAccordion>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6">Original Text</Typography>
              <Typography variant="body2" whiteSpace="break-spaces" mt={3}>
                {scraped.liveGig.originalText}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6">Base Text</Typography>
              <Typography variant="body2" whiteSpace="break-spaces" mt={3}>
                {scraped.liveGig.baseText}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
}

function MatchIcon({ match }: { match: boolean }) {
  return (
    <>
      {match ? (
        <CheckCircleIcon color="success" sx={{ width: '40px', height: '40px' }} />
      ) : (
        <CancelIcon color="error" sx={{ width: '40px', height: '40px' }} />
      )}
    </>
  );
}
